import React, { useState, useEffect } from 'react';
import {
  Phone, Clock, DollarSign, TrendingUp, Users, Activity,
  Calendar, RefreshCw, ArrowUp, ArrowDown, BarChart2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [overview, setOverview] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [agentStats, setAgentStats] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [realtime, setRealtime] = useState(null);

  useEffect(() => {
    loadAnalytics();
    
    // Refresh realtime data every 30 seconds
    const interval = setInterval(loadRealtime, 30000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      const [overviewRes, chartRes, agentsRes, hourlyRes, realtimeRes] = await Promise.all([
        api.get(`/analytics/overview?days=${timeRange}`),
        api.get(`/analytics/calls/chart?days=${timeRange}`),
        api.get(`/analytics/agents?days=${timeRange}`),
        api.get(`/analytics/calls/hourly?days=7`),
        api.get('/analytics/realtime')
      ]);
      
      setOverview(overviewRes.data.data);
      setChartData(chartRes.data.data || []);
      setAgentStats(agentsRes.data.data || []);
      setHourlyData(hourlyRes.data.data || []);
      setRealtime(realtimeRes.data.data);
    } catch (error) {
      console.error('Load analytics error:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadRealtime = async () => {
    try {
      const res = await api.get('/analytics/realtime');
      setRealtime(res.data.data);
    } catch (error) {
      console.error('Realtime refresh error:', error);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCost = (cost) => {
    return '$' + parseFloat(cost || 0).toFixed(4);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const maxChartValue = Math.max(...chartData.map(d => d.calls), 1);
  const maxHourlyValue = Math.max(...hourlyData.map(d => d.calls), 1);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Call performance and insights</p>
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(parseInt(e.target.value))}
            className="px-4 py-2 border rounded-lg"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={loadAnalytics}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Realtime Banner */}
      {realtime && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                <span className="font-medium">Live</span>
              </div>
              <div>
                <span className="text-2xl font-bold">{realtime.active_calls}</span>
                <span className="ml-2 text-blue-200">Active Calls</span>
              </div>
              <div>
                <span className="text-2xl font-bold">{realtime.calls_last_hour}</span>
                <span className="ml-2 text-blue-200">Last Hour</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-blue-200">Today</div>
              <div className="text-xl font-bold">{realtime.calls_today} calls</div>
              <div className="text-sm text-blue-200">{formatCost(realtime.cost_today)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Phone}
            label="Total Calls"
            value={overview.total_calls}
            subValue={`${overview.completed_calls} completed`}
            color="blue"
          />
          <StatCard
            icon={Clock}
            label="Avg Duration"
            value={formatDuration(overview.avg_duration_seconds)}
            subValue={`${Math.round(overview.total_duration_seconds / 60)} total mins`}
            color="green"
          />
          <StatCard
            icon={DollarSign}
            label="Total Cost"
            value={formatCost(overview.total_cost)}
            subValue={formatCost(parseFloat(overview.total_cost) / (overview.total_calls || 1)) + '/call'}
            color="purple"
          />
          <StatCard
            icon={Users}
            label="Active Agents"
            value={overview.total_agents}
            subValue={`${overview.provider_breakdown?.length || 0} providers`}
            color="orange"
          />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Call Volume Chart */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Call Volume</h3>
          <div className="h-64">
            {chartData.length > 0 ? (
              <div className="flex items-end justify-between h-full gap-1">
                {chartData.map((day, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                      style={{ height: `${(day.calls / maxChartValue) * 100}%`, minHeight: day.calls > 0 ? '4px' : '0' }}
                      title={`${day.calls} calls on ${day.date}`}
                    />
                    {chartData.length <= 14 && (
                      <span className="text-xs text-gray-400 mt-1 rotate-45 origin-left">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Hourly Distribution */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Hourly Distribution (Last 7 Days)</h3>
          <div className="h-64">
            {hourlyData.length > 0 ? (
              <div className="flex items-end justify-between h-full gap-0.5">
                {hourlyData.map((hour, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-green-500 rounded-t transition-all hover:bg-green-600"
                      style={{ height: `${(hour.calls / maxHourlyValue) * 100}%`, minHeight: hour.calls > 0 ? '4px' : '0' }}
                      title={`${hour.calls} calls at ${hour.label}`}
                    />
                    {idx % 4 === 0 && (
                      <span className="text-xs text-gray-400 mt-1">{hour.label}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agent Performance */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Agent Performance</h3>
        </div>
        
        {agentStats.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            No agent data available
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Agent</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Provider</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Calls</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Completed</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Success Rate</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Avg Duration</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {agentStats.map((agent) => (
                  <tr key={agent.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{agent.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        agent.provider === 'openai' ? 'bg-green-100 text-green-700' :
                        agent.provider === 'deepgram' ? 'bg-blue-100 text-blue-700' :
                        agent.provider === 'custom' ? 'bg-purple-100 text-purple-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {agent.provider}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">{agent.total_calls}</td>
                    <td className="px-4 py-3 text-right">{agent.completed_calls}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-medium ${
                        parseFloat(agent.success_rate) >= 90 ? 'text-green-600' :
                        parseFloat(agent.success_rate) >= 70 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {agent.success_rate}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">{formatDuration(agent.avg_duration)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCost(agent.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Provider Breakdown */}
      {overview?.provider_breakdown?.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Provider Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {overview.provider_breakdown.map((provider) => (
              <div key={provider.provider} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className={`text-2xl font-bold ${
                  provider.provider === 'openai' ? 'text-green-600' :
                  provider.provider === 'deepgram' ? 'text-blue-600' :
                  provider.provider === 'custom' ? 'text-purple-600' :
                  'text-orange-600'
                }`}>
                  {provider.call_count}
                </div>
                <div className="text-sm text-gray-600 capitalize">{provider.provider}</div>
                <div className="text-xs text-gray-400">{formatCost(provider.total_cost)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, subValue, color }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{label}</div>
          {subValue && (
            <div className="text-xs text-gray-400 mt-1">{subValue}</div>
          )}
        </div>
      </div>
    </div>
  );
}
